import React, { useState, useEffect } from 'react';
// custom components 
import HandScanner from './hand-scanner';
import { CustomSpinner } from '_atoms';
import { FormBookingCard, CustomModal, PassengerInformationForm, FlightInformationForm } from "_molecules";

// bootstrap
import { Button, Spinner, Modal } from 'react-bootstrap';

// styles 
import { Colors } from '_styles';

// redux
import { useSelector, useDispatch } from 'react-redux';
import { selectIata, selectUserDb } from '_redux/slices/main-screen/user';
import { setScreen } from '_redux/slices/main-screen/render-screen';
import { selectBookingForm, setBookingForm, reset as resetForm, selectFlightInformation, setFlightInformation } from '_redux/slices/assistance-bookings/booking-form';
import { resetPostedBooking, selectPatchBookingStatus, selectDeleteStatus, postError, deleteBookings, resetPostedBookingListStatus, patchBookingList } from '_redux/slices/prm-bookings/bookings';
import { addFlightPassenger, selectAddFlightPassengerStatus, resetAddFlightPassenger, selectAddFlightPassengerError, selectAddFlightPassengerBookings, selectAddFlightPassengerNeedsDepartureDate } from '_redux/slices/timeline/timeline-bookings';

//#region styles
// Shared card style for form sections
const sectionCard = {
    background: '#fff',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: 12,
    padding: '24px 28px',
    marginBottom: 20,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

const sectionHeader = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 20,
};

const stepBadge = (num) => ({
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
});

const sectionTitle = {
    fontSize: 16,
    fontWeight: 600,
    color: Colors.TEXT_COLOR,
    margin: 0,
};
//#endregion

const BookingForm = () => {

    //#region state variables 
    const [submissionError, setSubmissionError] = useState('');
    // modals
    const [confirmedBookingModal, setConfirmedBookingModal] = useState(false);
    const [showBooking, setShowBooking] = useState(false);
    const [providedDepartureDate, setProvidedDepartureDate] = useState('');
    const [providedAirportCode, setProvidedAirportCode] = useState('');
    const [providedOriginAirport, setProvidedOriginAirport] = useState('');         // airline accounts: origin airport for ambiguous flights
    const [providedDestinationAirport, setProvidedDestinationAirport] = useState(''); // airline accounts: destination airport for ambiguous flights
    const [additionalFlightDetailsErrors, setAdditionalFlightDetailsErrors] = useState({});
    // errors
    const [showDelete, setShowDelete] = useState(false);
    const bookingError = useSelector(postError);                                // holds error if booking info is wrong

    // redux
    const dispatch = useDispatch();
    const iata = useSelector(selectIata);                                       // holds iata of account
    const userDb = useSelector(selectUserDb);
    const isAirline = userDb?.details?.type === "AIRLINE";                       // airlines have no home airport (iata is the carrier code)
    const bookingForm = useSelector(selectBookingForm);                         // passenger info from passenger info form
    const flightForm = useSelector(selectFlightInformation);                    // flight info from flight form
    const patchStatus = useSelector(selectPatchBookingStatus)                   // status of updating bookings
    const deletedStatus = useSelector(selectDeleteStatus)                       // status of deleting a booking
    const flightPassengerBookings = useSelector(selectAddFlightPassengerBookings);
    const addFlightPassengerStatus = useSelector(selectAddFlightPassengerStatus);
    const addFlightPassengerError = useSelector(selectAddFlightPassengerError);
    const needsDepartureDate = useSelector(selectAddFlightPassengerNeedsDepartureDate);

    //#endregion

    //#region create booking handlers 
    // check if flight form has errors
    const checkFlightErrors = (flight) => {
        let flightErrors = {};

        // Carrier code: 2 alphanumeric chars + optional letter (e.g. "BA", "EZY", "6E")
        const carrierCodeRegex = /^[A-Za-z0-9]{2}[A-Za-z]?$/;
        // Flight number: 1-4 digits + optional suffix letter (e.g. "100", "5506", "1234A")
        const flightNumberRegex = /^\d{1,4}[A-Za-z]?$/;

        // check carrier code
        if (!flight.carrierCode || !carrierCodeRegex.test(flight.carrierCode)) {
            flightErrors['carrierCode'] = 'Please enter a valid carrier code'
        }
        // check flight number
        if (!flight.flightNumber || !flightNumberRegex.test(flight.flightNumber)) {
            flightErrors['flightNumber'] = 'Please enter a valid flight number'
        }
        // check departure date
        if (flight.flightDepartureDate.length == 0) {
            flightErrors['departureDate'] = 'Departure date required'
        }

        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.toISOString().split('T')[0];
        if (flight.flightDepartureDate < yesterday) {
            flightErrors['departureDate'] = 'Departure date cannot be before yesterday'
        }


        // set errors
        dispatch(setFlightInformation({ ...flight, errors: flightErrors }))

        // update flight errors
        if (Object.keys(flightErrors).length > 0) {
            return true;
        } else {
            return false;
        }
    }

    // check booking list for errors
    const checkBookingErrors = (booking) => {

        let errors = [];

        // go through list of bookings
        booking.forEach((passengerInformation, index) => {
            // check first and last name
            if (passengerInformation.firstName.length == 0) {
                let tempList = [...errors]; // copy list
                const newObj = Object.assign({}, tempList[index], { firstName: 'Valid first name required' });        // create a new object with a writable firstName property
                tempList[index] = newObj; // update list with new object
                errors = tempList;  // update error list with new error
            }
            if (passengerInformation.secondName.length == 0) {
                let tempList = [...errors]; // copy list
                const newObj = Object.assign({}, tempList[index], { secondName: 'Valid second name required' });        // create a new object with a writable firstName property
                tempList[index] = newObj; // update list with new object
                errors = tempList;  // update error list with new error
            }

            // check asssitances
            if (passengerInformation.assistance.length == 0) {
                let tempList = [...errors]; // copy list
                const newObj = Object.assign({}, tempList[index], { assistance: 'Please select at least one assistance' });        // create a new object with a writable firstName property
                tempList[index] = newObj; // update list with new object
                errors = tempList;  // update error list with new error
            }

            // check airport requests
            if (passengerInformation.originAssistance == false && passengerInformation.destinationAssistance == false) {
                let tempList = [...errors]; // copy list
                const newObj = Object.assign({}, tempList[index], { assistanceSelector: 'Assistance must be requested from at least one airport' });        // create a new object with a writable firstName property
                tempList[index] = newObj; // update list with new object
                errors = tempList;  // update error list with new error
            }
        });

        let tempBookingForm = bookingForm;

        // add errors to each booking matching index
        errors.forEach((bookingError, index) => {
            let tempList = [...tempBookingForm]; // copy list
            const newObj = Object.assign({}, tempList[index], { errors: bookingError })
            tempList[index] = newObj; // update list with new object
            tempBookingForm = tempList;
        })

        // update list
        dispatch(setBookingForm(tempBookingForm));

        if (errors.length > 0) { return true }
        else { return false; }
    }

    const checkAdditionalFlightDetailsErrors = () => {
        if (!needsDepartureDate || addFlightPassengerStatus !== 'needs_departure_date') {
            setAdditionalFlightDetailsErrors({});
            return false;
        }

        const errors = {};
        const airportCodeRegex = /^[A-Za-z]{3}$/;

        if (!providedDepartureDate) {
            errors['departureDate'] = 'Departure date required';
        }

        if (isAirline) {
            // Airline accounts have no home airport — both endpoints must be provided
            if (!providedOriginAirport || !airportCodeRegex.test(providedOriginAirport.trim())) {
                errors['originAirport'] = 'Origin airport must be 3 letters';
            }
            if (!providedDestinationAirport || !airportCodeRegex.test(providedDestinationAirport.trim())) {
                errors['destinationAirport'] = 'Destination airport must be 3 letters';
            }
        } else if (!providedAirportCode || !airportCodeRegex.test(providedAirportCode.trim())) {
            errors['airportCode'] = 'Airport code must be 3 letters';
        }

        setAdditionalFlightDetailsErrors(errors);
        return Object.keys(errors).length > 0;
    }

    const clearAdditionalFlightDetailsError = (field) => {
        if (!additionalFlightDetailsErrors[field]) return;

        const errors = { ...additionalFlightDetailsErrors };
        delete errors[field];
        setAdditionalFlightDetailsErrors(errors);
    }

    const resolveManualAirportDetails = () => {
        const { flightDepartureDate, flightType } = flightForm;
        const normalizedAirportCode = providedAirportCode.trim().toUpperCase();
        const normalizedOrigin = providedOriginAirport.trim().toUpperCase();
        const normalizedDestination = providedDestinationAirport.trim().toUpperCase();

        let additionalAirportDetails = {};
        let originValue = '';
        let destinationValue = '';

        if (needsDepartureDate) {
            if (isAirline) {
                if (normalizedOrigin && normalizedDestination) {
                    additionalAirportDetails = { origin: normalizedOrigin, destination: normalizedDestination };
                    originValue = normalizedOrigin;
                    destinationValue = normalizedDestination;
                }
            } else if (normalizedAirportCode) {
                additionalAirportDetails = flightType === "INBOUND"
                    ? { origin: normalizedAirportCode, destination: iata }
                    : { origin: iata, destination: normalizedAirportCode };
                originValue = additionalAirportDetails.origin;
                destinationValue = additionalAirportDetails.destination;
            }
        }

        const manualDepartureDate = providedDepartureDate || flightDepartureDate;

        return {
            additionalAirportDetails,
            originValue,
            destinationValue,
            manualDepartureDate,
        };
    }

    const buildManualSourceMetadata = () => {
        const { originValue, destinationValue, manualDepartureDate } = resolveManualAirportDetails();
        const hasManualAirports = Boolean(originValue && destinationValue);

        if (!needsDepartureDate || !hasManualAirports) {
            return {
                source: "PORTAL",
                data: {}
            };
        }

        return {
            source: "PORTAL",
            data: {},
            manuallySetIATAs: true,
            originValue,
            destinationValue,
            departureDate: manualDepartureDate,
        };
    }

    // create document for request
    const createBookingDocument = (booking) => {
        //I removed line that switches EZY->U2, we have already deal with that in the server side, There are other CC to handle like RR,TOM,etc.    
        const { carrierCode, flightNumber, flightDepartureDate, flightType } = flightForm;
        let cc = carrierCode.toUpperCase()
        if (carrierCode == 'EZY' || carrierCode == 'EJU') {
            cc = 'U2'
        }

        const { additionalAirportDetails } = resolveManualAirportDetails();

        const flightDetails = {
            carrierCode: cc,
            flightNumber: flightNumber,
            // Review this section with Mohamed, to decide if we should use arrival or departure date, with first attempt
            ...(flightType === "INBOUND"
                ? { arrivalDate: flightDepartureDate, ...(providedDepartureDate && { departureDate: providedDepartureDate }), ...additionalAirportDetails }
                : { departureDate: flightDepartureDate, ...additionalAirportDetails })
        }

        const bookingDoc = {
            name: booking.firstName.trim() + ' ' + booking.secondName.trim(),
            flightDetails,
            assistance: booking.assistance,
            referenceNumber: booking.referenceNumber,
            destinationAssistance: booking.destinationAssistance,
            originAssistance: booking.originAssistance,
            airlineConfirmed: userDb.details.type === "AIRLINE" ? true : booking.airlineConfirmed,
            source: buildManualSourceMetadata()
        }
        if (booking.ambiliftRequired) bookingDoc['ambiliftRequired'] = true;
        if (booking.equipment) {
            bookingDoc['equipment'] = booking.equipment
            bookingDoc['equipmentInformation'] = {
                battery: booking.battery.value,
                dimensions: {
                    height: +(booking.height),
                    width: +(booking.width)
                },
                make: booking.make,
                model: booking.model,
                weight: +(booking.weight)
            }

        }
        return bookingDoc;
    }

    // handle creating bookings
    const handleBooking = () => {

        let flightErrors = checkFlightErrors(flightForm);
        let bookingsErrors = checkBookingErrors(bookingForm);
        let hasAdditionalFlightDetailsErrors = checkAdditionalFlightDetailsErrors();

        // if flight form and booking form is valid created booking documents and send request
        if (!flightErrors && !bookingsErrors && !hasAdditionalFlightDetailsErrors) {

            setSubmissionError("");

            if (needsDepartureDate && addFlightPassengerStatus === 'needs_departure_date') {
                const { originValue, destinationValue, manualDepartureDate } = resolveManualAirportDetails();
                const confirmed = window.confirm(
                    `Confirm route: ${originValue} → ${destinationValue} on ${manualDepartureDate}.\n\nIncorrect origin or destination cannot be edited after create and passengers will not appear under the correct flight in the timeline.`
                );
                if (!confirmed) {
                    return;
                }
            }

            // create list of booking docs
            let list = [];
            bookingForm.forEach(booking => {
                let doc = createBookingDocument(booking)
                list.push(doc)
            });
            // post booking list
            dispatch(addFlightPassenger({
                bookings: list,
                iata: iata
            }));

        }
        else {
            // show error message
            if (flightErrors && bookingsErrors) { setSubmissionError('Enter correct flight and passenger information') }
            else if (flightErrors) { setSubmissionError('Enter the correct flight information') }
            else if (hasAdditionalFlightDetailsErrors) { setSubmissionError('Enter the requested departure date and airport code') }
            else { setSubmissionError('Enter the correct passenger information') }
        }
    }

    // confirm bookings
    const verifyBooking = () => {
        if (flightPassengerBookings && flightPassengerBookings.length > 0) {
            let tempList = [];
            flightPassengerBookings.forEach((booking) => {
                if (booking._id) {
                    // only patch valid bookings
                    const copy = { ...booking }
                    copy["status"] = "user-confirmed";
                    tempList.push(copy)
                }
            })
            dispatch(patchBookingList(tempList));
        };
    }

    // delete booking using list of ids
    const handleDelete = () => {
        let idList = [];
        flightPassengerBookings.forEach(booking => {
            if (booking && booking._id) { idList.push(booking._id) }
        });
        dispatch(deleteBookings(idList))
    }

    // handle additional passenger
    const addPassenger = () => {
        let tempList = [...bookingForm]
        const emptyObject = {
            firstName: "",
            secondName: "",
            assistance: "",
            carrierCode: "",
            flightNumber: "",
            referenceNumber: "",
            dueTime: "",
            prmBio: "",
            originAssistance: true,
            destinationAssistance: true,
            airlineConfirmed: false,

            make: "",
            model: "",
            weight: "",
            battery: "",
            width: "",
            height: "",

            equipment: false,
            additionalPassenger: false,
            errors: []
        }
        tempList.push(emptyObject);

        dispatch(setBookingForm(tempList))
    }
    //#endregion


    // on inital render reset all flags
    useEffect(() => {
        setShowBooking(false);
        setConfirmedBookingModal(false);
        setSubmissionError("")
        dispatch(resetForm());
        dispatch(resetPostedBooking());
        dispatch(resetPostedBookingListStatus());
    }, []);

    useEffect(() => {
        dispatch(resetAddFlightPassenger())
    }, [])

    // monitor post status and show bookings if post succeeds
    useEffect(() => {
        if (addFlightPassengerStatus == 'succeeded') {
            // show success modal
            setShowBooking(true)
        }
        else {
            setShowBooking(false)
        }
    }, [addFlightPassengerStatus]);

    useEffect(() => {

        if (addFlightPassengerError && addFlightPassengerStatus === "failed") {
            setSubmissionError(addFlightPassengerError)
        }
    }, [addFlightPassengerError, addFlightPassengerStatus])


    // monitor patch status
    useEffect(() => {
        if (patchStatus == 'succeeded') {
            // show success modal
            setShowBooking(false);
            setConfirmedBookingModal(true);
            dispatch(resetForm());
            dispatch(resetPostedBooking());
            dispatch(resetPostedBookingListStatus());
            // clear per-submission flight-detail prompts so the NEXT booking
            // re-prompts instead of silently reusing the last-entered value
            setProvidedAirportCode('');
            setProvidedOriginAirport('');
            setProvidedDestinationAirport('');
            setProvidedDepartureDate('');
            dispatch(resetAddFlightPassenger());   // clears needsDepartureDate + prior bookings
        }
    }, [patchStatus]);

    // monitor delete status
    useEffect(() => {
        if (deletedStatus == 'succeeded') {
            // show success modal
            setShowBooking(false);
            setShowDelete(true);
            dispatch(resetForm());
            dispatch(resetPostedBookingListStatus());
        }
    }, [deletedStatus]);


    // reset slices
    useEffect(() => {
        dispatch(resetPostedBooking());
        dispatch(resetPostedBookingListStatus());
        dispatch(resetForm());
    }, []);


    return (
        <>
            {/* Loading State */}
            {addFlightPassengerStatus == 'loading' ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
                    <CustomSpinner />
                </div>
            ) : showBooking ? (
                /* ── Booking Confirmation View ── */
                <div>
                    <div style={sectionCard}>
                        <div style={sectionHeader}>
                            <div style={stepBadge()}>✓</div>
                            <h3 style={sectionTitle}>Review & Confirm Bookings</h3>
                        </div>
                        <FormBookingCard bookings={flightPassengerBookings || []} />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, gap: 12 }}>
                        <Button
                            variant='outline-danger'
                            disabled={deletedStatus == 'succeeded'}
                            onClick={handleDelete}
                            style={formStyles.cancelButton}
                        >
                            {deletedStatus == 'loading' ? <CustomSpinner /> : 'Cancel Bookings'}
                        </Button>
                        <Button
                            disabled={addFlightPassengerStatus != 'succeeded'}
                            onClick={verifyBooking}
                            style={formStyles.confirmButton}
                        >
                            {addFlightPassengerStatus == 'loading' ? <CustomSpinner /> : 'Confirm Bookings'}
                        </Button>
                    </div>
                    {patchStatus && patchStatus !== 'idle' && (
                        <div style={formStyles.errorBanner}>
                            <p style={formStyles.errorText}>{patchStatus}</p>
                        </div>
                    )}
                </div>
            ) : (
                /* ── Create Booking Form ── */
                <div>
                    {/* Step 1: Passenger Information */}
                    <div style={sectionCard}>
                        <div style={sectionHeader}>
                            <div style={stepBadge()}>1</div>
                            <h3 style={sectionTitle}>Passenger Information</h3>
                        </div>

                        {bookingForm.length > 0 && bookingForm.map((_, index) => (
                            <PassengerInformationForm key={index} index={index} />
                        ))}

                        {bookingForm.length < 8 && (
                            <button onClick={addPassenger} style={formStyles.addPassengerBtn}>
                                <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
                                <span>Add Another Passenger</span>
                            </button>
                        )}
                    </div>

                    {/* Step 2: Flight Information */}
                    <div style={sectionCard}>
                        <div style={sectionHeader}>
                            <div style={stepBadge()}>2</div>
                            <h3 style={sectionTitle}>Flight Information</h3>
                        </div>
                        {/* For now, still manual input disable, as Conor wants */}
                        <FlightInformationForm
                            manualFlightDisabled={false}
                            manualCarrierDisabled={true}
                            isAirline={isAirline}
                            setProvidedDepartureDate={setProvidedDepartureDate}
                            setProvidedAirportCode={setProvidedAirportCode}
                            setProvidedOriginAirport={setProvidedOriginAirport}
                            setProvidedDestinationAirport={setProvidedDestinationAirport}
                            additionalFlightDetailsErrors={additionalFlightDetailsErrors}
                            clearAdditionalFlightDetailsError={clearAdditionalFlightDetailsError}
                        />
                    </div>

                    {/* Step 3: Boarding Pass Scanner */}
                    <div style={{ ...sectionCard, background: Colors.GRAY_LIGHT, border: `1px dashed ${Colors.GRAY_MEDIUM}` }}>
                        <div style={sectionHeader}>
                            <div style={{ ...stepBadge(), backgroundColor: Colors.GRAY_DARK }}>
                                <span style={{ fontSize: 14 }}>⎘</span>
                            </div>
                            <h3 style={sectionTitle}>Quick Fill — Boarding Pass Scanner</h3>
                            <span style={{ fontSize: 12, color: Colors.GRAY_DARK, fontWeight: 400, marginLeft: 4 }}>Optional</span>
                        </div>
                        <HandScanner />
                    </div>

                    {/* Submit Section */}
                    <div style={formStyles.submitSection}>
                        <Button
                            onClick={handleBooking}
                            disabled={addFlightPassengerStatus == 'loading'}
                            style={formStyles.submitButton}
                        >
                            {addFlightPassengerStatus == 'loading' ? (
                                <><CustomSpinner /><span className="visually-hidden">Loading...</span></>
                            ) : (
                                'Create Booking'
                            )}
                        </Button>

                        {submissionError.length > 0 && (
                            <div style={formStyles.errorBanner}>
                                <p style={formStyles.errorText}>{submissionError}</p>
                            </div>
                        )}
                        {bookingError && (
                            <div style={formStyles.errorBanner}>
                                <p style={formStyles.errorText}>{bookingError}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Successfully Submitted Booking Modal */}
            <CustomModal
                hide={() => { setConfirmedBookingModal(false) }}
                show={confirmedBookingModal}
                title={'Successfully Confirmed Booking'}
                body={'This booking has been confirmed and assistance requests have been sent to both departure and arrival airports'}
                button={'Done'}
                openButton={() => { setConfirmedBookingModal(false) }}
            />

            {/* Delete Booking Modal */}
            <Modal show={showDelete} onHide={() => { setShowDelete(false); dispatch(resetPostedBooking()); dispatch(resetForm()); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Deleted Booking</Modal.Title>
                </Modal.Header>
                <Modal.Body>Your booking has been successfully deleted</Modal.Body>
                <Modal.Footer>
                    <Button variant='outline-primary' onClick={() => { setShowDelete(false); dispatch(resetPostedBooking()); dispatch(setScreen('assistance-bookings')); }}>
                        {deletedStatus == 'loading' ? (
                            <Spinner as="span" animation="border" role="status" aria-hidden="true" variant="primary" />
                        ) : (
                            'Done'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

const formStyles = {
    addPassengerBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 20px',
        border: `1.5px dashed ${Colors.PRM_LIGHT_BLUE}`,
        borderRadius: 8,
        background: Colors.PRM_BLUE_ACCENT,
        color: Colors.PRM_LIGHT_BLUE,
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 500,
        marginTop: 16,
        transition: 'all 0.15s ease',
    },
    submitSection: {
        marginTop: 8,
        paddingTop: 20,
    },
    submitButton: {
        padding: '12px 40px',
        fontSize: 15,
        fontWeight: 600,
        borderRadius: 8,
        border: 'none',
        backgroundColor: Colors.PRM_LIGHT_BLUE,
        color: '#fff',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        boxShadow: '0 2px 8px rgba(48, 127, 226, 0.3)',
    },
    cancelButton: {
        padding: '10px 28px',
        fontSize: 14,
        fontWeight: 500,
        borderRadius: 8,
    },
    confirmButton: {
        padding: '10px 28px',
        fontSize: 14,
        fontWeight: 600,
        borderRadius: 8,
        border: 'none',
        backgroundColor: Colors.DARK_GREEN,
        color: '#fff',
        cursor: 'pointer',
    },
    errorBanner: {
        marginTop: 12,
        padding: '10px 14px',
        background: Colors.LIGHT_RED,
        borderRadius: 8,
        borderLeft: `4px solid ${Colors.DARK_RED}`,
    },
    errorText: {
        color: Colors.DARK_RED,
        margin: 0,
        fontSize: 13,
        fontWeight: 500,
    },
};

export default BookingForm;
